import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, Plus, Trash2, RefreshCw, Upload, X, Pencil, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { chunkedVideoUpload } from '../utils/chunkedUpload';

const API = process.env.REACT_APP_BACKEND_URL;

// Avatar can be either a full URL (legacy create flow accepts `avatar_url`)
// or a relative object-storage path (`avatars/{id}/xxx.jpg`) when uploaded
// via the dedicated avatar endpoint. Normalise both into something <img> can load.
const resolveAvatar = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  return `${API}/api/files/${avatar}`;
};

/**
 * Admin-only Seed Creator management tab.
 *
 * Lets the admin (a) create demo "seed" creator accounts and (b) upload
 * videos as any seed creator without ever logging in as that account. Seed
 * creators are excluded from analytics and never consume Founding Creator
 * seats, but their published videos appear on Home / Discover / Pulse just
 * like real videos.
 */
export const SeedTab = ({ axiosInstance }) => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null); // creator object or null
  const [showEdit, setShowEdit] = useState(null);     // creator object or null

  const fetchCreators = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/seed/creators');
      setCreators(res.data.creators || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load seed creators');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { fetchCreators(); }, [fetchCreators]);

  const handleDelete = async (creator) => {
    if (!window.confirm(`Delete seed creator "${creator.display_name}" and all their videos? This cannot be undone.`)) return;
    try {
      const res = await axiosInstance.delete(`/admin/seed/creators/${creator.id}`);
      toast.success(`Deleted seed creator + ${res.data.videos_removed} video(s)`);
      fetchCreators();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="seed-tab">
      {/* Header + create button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-300" />
            Seed Creators
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Demo creators owned by you. Excluded from all analytics. Don&apos;t consume Founding seats.
            Videos still appear publicly on Home / Discover / Pulse.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchCreators}
            disabled={loading}
            className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="seed-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-medium inline-flex items-center gap-1.5"
            data-testid="seed-create-btn"
          >
            <Plus className="w-4 h-4" />
            Create Seed Creator
          </button>
        </div>
      </div>

      {/* Creators list */}
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
        {creators.length === 0 && !loading && (
          <div className="px-5 py-12 text-center text-zinc-500 text-sm" data-testid="seed-empty">
            No seed creators yet. Click &quot;Create Seed Creator&quot; to add one.
          </div>
        )}
        <div className="divide-y divide-zinc-800">
          {creators.map((c) => (
            <div key={c.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`seed-row-${c.username}`}>
              <div className="flex items-center gap-3 min-w-0">
                {c.avatar ? (
                  <img src={resolveAvatar(c.avatar)} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {(c.display_name || c.username || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{c.display_name}</p>
                  <p className="text-xs text-zinc-500 truncate">
                    @{c.username} · {c.video_count} video{c.video_count !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowEdit(c)}
                  className="px-3 py-1.5 rounded bg-zinc-700/60 hover:bg-zinc-700 text-zinc-200 text-xs font-medium inline-flex items-center gap-1"
                  data-testid={`seed-edit-${c.username}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setShowUpload(c)}
                  className="px-3 py-1.5 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 text-xs font-medium inline-flex items-center gap-1"
                  data-testid={`seed-upload-${c.username}`}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload as
                </button>
                <button
                  onClick={() => handleDelete(c)}
                  className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
                  title="Delete seed creator + all their videos"
                  data-testid={`seed-delete-${c.username}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateSeedModal
          axiosInstance={axiosInstance}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchCreators(); }}
        />
      )}

      {/* Upload-as modal */}
      {showUpload && (
        <UploadAsSeedModal
          axiosInstance={axiosInstance}
          creator={showUpload}
          onClose={() => setShowUpload(null)}
          onUploaded={() => { setShowUpload(null); fetchCreators(); }}
        />
      )}

      {/* Edit modal */}
      {showEdit && (
        <EditSeedModal
          axiosInstance={axiosInstance}
          creator={showEdit}
          onClose={() => setShowEdit(null)}
          onSaved={() => { setShowEdit(null); fetchCreators(); }}
        />
      )}
    </div>
  );
};

const CreateSeedModal = ({ axiosInstance, onClose, onCreated }) => {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!username.trim() || !displayName.trim()) {
      toast.error('Username and display name are required.');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post('/admin/seed/creators', {
        username: username.trim().toLowerCase(),
        display_name: displayName.trim(),
        bio: bio.trim(),
        avatar_url: avatarUrl.trim() || null,
      });
      toast.success('Seed creator created');
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-md bg-zinc-900 rounded-2xl border border-purple-500/30 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <p className="font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-300" /> Create Seed Creator
          </p>
          <button onClick={onClose} className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300" data-testid="seed-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Username *</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="aria_demo"
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
              data-testid="seed-create-username"
            />
            <p className="text-[11px] text-zinc-500 mt-1">3–30 chars, lowercase letters, numbers, underscores.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Display Name *</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Aria"
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
              data-testid="seed-create-displayname"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="AI musician from Brooklyn…"
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Avatar URL (optional)</label>
            <input
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://…/avatar.jpg"
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm">Cancel</button>
            <button
              onClick={submit}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-medium disabled:opacity-50"
              data-testid="seed-create-submit"
            >
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadAsSeedModal = ({ axiosInstance, creator, onClose, onUploaded }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('pop');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!videoFile) { toast.error('Pick a video file'); return; }
    if (!title.trim()) { toast.error('Title is required'); return; }
    setUploading(true);
    setProgress(0);
    try {
      const result = await chunkedVideoUpload({
        axiosInstance,
        file: videoFile,
        fields: {
          title: title.trim(),
          genre,
          description: '',
          tools_used: '',
          style: '',
          thumbnail_timestamp: 1.0,
          ai_disclosure: JSON.stringify({ category: 'ai_video', tools: [{ key: 'other', name: 'Demo' }] }),
          seed_creator_id: creator.id,
        },
        onProgress: (p) => setProgress(p),
      });
      toast.success(`Uploaded "${title}" as ${creator.display_name}`);
      onUploaded(result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-md bg-zinc-900 rounded-2xl border border-cyan-500/30 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <p className="font-semibold text-white flex items-center gap-2">
            <Upload className="w-4 h-4 text-cyan-300" /> Upload as @{creator.username}
          </p>
          <button onClick={onClose} className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300" disabled={uploading}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Video file *</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setVideoFile(e.target.files[0])}
              className="w-full text-sm text-zinc-200"
              data-testid="seed-upload-file"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Song title"
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
              data-testid="seed-upload-title"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Genre</label>
            <select
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
            >
              <option value="pop">Pop</option>
              <option value="rock">Rock</option>
              <option value="hip-hop">Hip-Hop</option>
              <option value="electronic">Electronic</option>
              <option value="r&b">R&amp;B</option>
              <option value="country">Country</option>
              <option value="indie">Indie</option>
              <option value="metal">Metal</option>
              <option value="ambient">Ambient</option>
              <option value="other">Other</option>
            </select>
          </div>
          {uploading && (
            <div>
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-fuchsia-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={uploading} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm disabled:opacity-50">Cancel</button>
            <button
              onClick={submit}
              disabled={uploading || !videoFile || !title.trim()}
              className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold disabled:opacity-50"
              data-testid="seed-upload-submit"
            >
              {uploading ? 'Uploading…' : 'Upload & Publish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const EditSeedModal = ({ axiosInstance, creator, onClose, onSaved }) => {
  const [username, setUsername] = useState(creator.username || '');
  const [displayName, setDisplayName] = useState(creator.display_name || '');
  const [bio, setBio] = useState(creator.bio || '');
  const [avatarPreview, setAvatarPreview] = useState(resolveAvatar(creator.avatar));
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Pick an image file'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setPendingAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!displayName.trim() || !username.trim()) {
      toast.error('Username and display name are required.');
      return;
    }
    setSaving(true);
    try {
      // 1. Upload the avatar first (if changed) so the field is fresh in DB
      if (pendingAvatarFile) {
        const fd = new FormData();
        fd.append('avatar', pendingAvatarFile);
        await axiosInstance.post(`/admin/seed/creators/${creator.id}/avatar`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // 2. Patch text fields
      await axiosInstance.patch(`/admin/seed/creators/${creator.id}`, {
        username: username.trim().toLowerCase(),
        display_name: displayName.trim(),
        bio: bio,
      });
      toast.success('Seed creator updated');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-md bg-zinc-900 rounded-2xl border border-purple-500/30 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <p className="font-semibold text-white flex items-center gap-2">
            <Pencil className="w-4 h-4 text-purple-300" /> Edit Seed Creator
          </p>
          <button onClick={onClose} className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300" data-testid="seed-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Avatar with click-to-upload */}
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative group"
              data-testid="seed-edit-avatar-btn"
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="" className="w-24 h-24 rounded-full object-cover ring-2 ring-purple-500/50" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white text-3xl font-semibold">
                  {(displayName || username || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                <Camera className="w-6 h-6" />
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarPick}
              className="hidden"
              data-testid="seed-edit-avatar-input"
            />
            <p className="text-xs text-zinc-500">Click avatar to change · JPG/PNG/WebP up to 5MB</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
              data-testid="seed-edit-username"
            />
            <p className="text-xs text-zinc-600 mt-1">lowercase a–z, 0–9, _ only</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Display name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={60}
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white"
              data-testid="seed-edit-displayname"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={280}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white resize-none"
              data-testid="seed-edit-bio"
            />
            <p className="text-xs text-zinc-600 mt-1 text-right">{bio.length}/280</p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving || !displayName.trim() || !username.trim()}
              className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="seed-edit-submit"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
